/**
 * Component e2-dialog is used to show some content over the main page inside the dialog window
 *
 * @attr {string} name - Unique dialog identifier
 * @attr {string} [addClass] - css class added to the dialog html element inside shadow root
 * @attr {number} [close-timer] - auto close dialog after defined time in ms
 * @attr {boolean} [close-bg-click] - defines if the dialog should be closed on click by background
 * @attr {boolean} [animation] - defines if hiding of the dialog should be animated
 * @attr {boolean} [no-scroll] - defines if the dialog shouldn't have the scroll
 * @attr {boolean} [is-connected] - defines if the dialog takes the data from the store
 * @attr {boolean} [adjust-iframe-height-to-content] - defines if the iframe height should be adjusted to content
 * @attr {boolean} [adjust-iframe-width-to-widest-img] - defines if the iframe width should be adjusted to the widest image
 * @attr {boolean} [open-links-in-main-window] - defines if the links in iframe should be opened in the main window
 * @attr {boolean} [should-be-opened] - defines if the dialog should be automatically opened
 * @attr {string} [close-dispatch-action] - when close button is clicked, action with this type will be dispatched
 * @attr {boolean} [iframe-stop-video-on-close] - if your dialog has an iframe with video and you want to stop it on dialog closing
 * @attr {string} [iframeComponentSelector] - if you use the [iframe-stop-video-on-close] attribute and you have a custom component
 * @attr {string} [show-content-from-top] - the content will be shown from top without saving previous scroll position
 *
 * Properties for the buttons
 * @attr {string} [btn-success] - the text of the success button
 * @attr {boolean} [no-arrow] - defines if the buttons should have arrow
 * @attr {boolean} [btn-full-width] - defines if the buttons should be full width
 * @attr {string} [btn-success-img] - the image of the success button
 * @attr {string} [class-success] - the css class of the success button
 * @attr {Boolean} [redirect-on-success] - if your dialog has redirect link and you want to redirect on success
 * @attr {string} [btn-cancel] - the text of the cancel button
 * @attr {string} [btn-cancel-img] - the image of the cancel button
 * @attr {string} [class-cancel] - the css class of the cancel button
 * @attr {boolean} [btn-close] - defines if the icon close button at the right top corner should be rendered
 * @attr {boolean} [btn-close-with-redirect] - defines if the icon close button should do a redirect
 * @attr {boolean} [cancel-as-label] - defines if instead of cancelButton a label should be rendered
 *
 * @prop {boolean} open - readonly property defines if the dialog is opened; is set from the store
 * @prop {string} iframeUrl - the url of the iframe opened in the dialog
 *
 * HTML Structure Example
 * <e2-dialog name="preview-popup" addClass="dialog--preview-popup" close-bg-click="true" no-scroll>
 *     <div slot="top">
 *         <!-- DIALOG HEADER HERE -->
 *     </div>
 *     <div slot="content">
 *         <!-- CONTENT HERE -->
 *     </div>
 * </e2-dialog>
 */

import { LitElement, css, html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import bodyScrollLock from '@elab/core/utils/scroll-lock';
import sanitizeHTML from '@elab/core/utils/sanitizeHTML';
import FormInputStyles from '@elab/styles/web-components/forms/FormInput.scss';
import dialogStyles from '@elab/styles/web-components/dialog.scss';
import barcodePopupStyles from '@elab/styles/web-components/barcode-popup.scss';
import connect from '@elab/core/store/connect';
import { actions, selectors } from '@elab/core/store/ui';
import { actions as globalActions } from '@elab/core/store/global';
import { actions as rtsActions } from '@elab/components/connected/real-time-stock/redux';
import { actions as storeLocatorAction } from '@elab/core/store/store-locator';
import { selectors as configSelectors } from '@elab/core/store/config';
import { buildBlockModificationClass } from '@elab/core/utils/bem';

export class Dialog extends LitElement {
    static get styles() {
        return [css([dialogStyles]), css([barcodePopupStyles]), css([FormInputStyles])];
    }

    static get properties() {
        return {
            name: { type: String },
            addClass: { type: String },
            closeTimerMs: { type: Number, attribute: 'close-timer' },
            closeOnBgClick: { type: Boolean, attribute: 'close-bg-click' },
            animation: { type: Boolean },
            noScroll: { type: Boolean, attribute: 'no-scroll' },
            isConnected: { type: Boolean, attribute: 'is-connected' },
            adjustIframeHeightToContent: {
                type: Boolean,
                attribute: 'adjust-iframe-height-to-content',
            },
            adjustIframeWidthToWidestImg: {
                type: Boolean,
                attribute: 'adjust-iframe-width-to-widest-img',
            },
            openLinksInMainWindow: {
                type: Boolean,
                attribute: 'open-links-in-main-window',
            },
            successButton: { type: String, attribute: 'btn-success' },
            fullWidthButton: { type: Boolean, attribute: 'btn-full-width' },
            successButtonImage: { type: String, attribute: 'btn-success-img' },
            successClass: { type: String, attribute: 'class-success' },
            redirectOnSuccess: {
                type: Boolean,
                attribute: 'redirect-on-success',
            },
            cancelButton: { type: String, attribute: 'btn-cancel' },
            cancelButtonImage: { type: String, attribute: 'btn-cancel-img' },
            cancelClass: { type: String, attribute: 'class-cancel' },
            closeButton: { type: Boolean, attribute: 'btn-close' },
            closeButtonInsideDialog: {
                type: Boolean,
                attribute: 'close-button-inside-dialog',
            },
            closeButtonText: { type: String, attribute: 'btn-close-text' },
            closeButtonWithRedirect: {
                type: String,
                attribute: 'btn-close-with-redirect',
            },
            closeButtonClass: { type: String, attribute: 'close-button-class' },
            buttonsSize: { type: String, attribute: 'buttons-size' },
            shouldBeOpened: { type: Boolean, attribute: 'should-be-opened' },
            cancelAsLabel: { type: Boolean, attribute: 'cancel-as-label' },
            open: { type: Boolean, attribute: false },
            iframeUrl: { type: String, attribute: false },
            iframeRedirectOnClose: { type: String, attribute: false },
            noBackground: { type: Boolean, attribute: 'no-background' },
            allowBodyScroll: { type: Boolean, attribute: 'allow-body-scroll' },
            windowSizeHeight: { type: Number, attribute: false },
            closeDispatchAction: {
                type: String,
                attribute: 'close-dispatch-action',
            },
            iframeStopVideoOnClose: {
                type: Boolean,
                attribute: 'iframe-stop-video-on-close',
            },
            iframeComponentSelector: {
                type: String,
                attribute: 'iframe-component-selector',
            },
            customHandleClose: { type: Boolean, attribute: false },
            noArrow: { type: Boolean, attribute: 'no-arrow' },
            scrollableArea: { type: String, attribute: 'scrollable-area' },
            analyticsConfig: { type: String, attribute: 'analytics-config' },
            closeButtonOutsideContent: { type: Boolean, attribute: 'close-button-outside-content' },
            gtmTypeClose: { type: String, attribute: 'gtm-type-close' },
            showContentFromTop: { type: Boolean, attribute: 'show-content-from-top' },
            initialized: { type: Boolean, reflect: true },
            iframeTitle: { type: String, attribute: 'iframe-title' },
        };
    }

    constructor() {
        super();

        this.name = '';
        this.addClass = '';
        this.closeTimerMs = 0;
        this.windowSizeHeight = window.innerHeight;
        this.closeOnBgClick = false;
        this.animation = false;
        this.cancelButton = '';
        this.cancelButtonImage = '';
        this.fullWidthButton = false;
        this.shouldBeOpened = false;
        this.allowBodyScroll = false;
        this.closeButtonInsideDialog = false;
        this.adjustIframeHeightToContent = false;
        this.buttonsSize = '';
        this.successButton = '';
        this.successButtonImage = '';
        this.redirectOnSuccess = false;
        this.cancelClass = '';
        this.successClass = '';
        this.closeButton = false;
        this.closeButtonClass = '';
        this.cancelAsLabel = false;
        this.noScroll = false;
        this.iframeUrl = false;
        this.iframeInitialized = false;
        this.closeDispatchAction = '';
        this.iframeStopVideoOnClose = false;
        this.iframeComponentSelector = '';
        this.noArrow = false;
        this.analyticsConfig = '';
        this.gtmTypeClose = '';
        this.closeButtonOutsideContent = false;
        /*
            In case of pointer dragging the object below will indicate whether
            mousedown/mouseup occurred inside dialog
            possible cases:
                - false, false - both occurred outside dialog
                - true, false - button pressed inside dialog, pointer dragged, button released outside dialog
                - true, true - both occurred inside dialog
        */
        this.pointerIsInDialog = {
            down: null,
            up: null,
        };
        this.showContentFromTop = false;
        this.iframeTitle = '';
    }

    get dialogClassMap() {
        const additionalClasses = this.addClass
            .split(' ')
            .reduce((acc, curr) => (curr ? { ...acc, [curr]: true } : acc), {});
        return {
            dialog: true,
            'dialog--no-background': this.noBackground,
            ...additionalClasses,
        };
    }

    beforeDisconnect() {
        if (this.name == 'rts-pickup-pdp') {
            this.doClose();
        }
    }

    render() {
        const { isConnected, content, iframeUrl } = this;
        const renderIframe = isConnected && iframeUrl;
        const renderContentFromRedux = !renderIframe && isConnected;
        const renderContentFromSlot = !renderIframe && !renderContentFromRedux;
        return html`
            <div
                @close=${this.doClose}
                class="${classMap(this.dialogClassMap)}"
                data-element="dialog"
            >
                ${this.closeButtonOutsideContent
                    ? this.getCloseTemplate({
                          text: this.closeButtonText,
                          addClass: this.closeButtonClass,
                      })
                    : null}
                <slot name="top"></slot>
                ${this.getScrollTemplate(html`
                    ${renderIframe ? this.getIframeTemplate() : null}
                    ${renderContentFromRedux ? html([sanitizeHTML(content)]) : null}
                    ${renderContentFromSlot
                        ? html`
                              <slot name="content"></slot>
                          `
                        : null}
                    ${this.renderButtons()}
                `)}
            </div>
            <div class="dialog-backdrop"></div>
        `;
    }

    renderButtons() {
        const {
            cancelButton,
            successButton,
            closeButton,
            closeButtonText,
            cancelButtonImage,
            successButtonImage,
            handleCancel,
            handleSuccess,
            successClass,
            cancelClass,
            closeButtonClass,
            cancelAsLabel,
        } = this;

        const successButtonHTML = successButton
            ? this.getButtonTemplate({
                  handle: handleSuccess,
                  text: successButton,
                  addClass: successClass,
                  img: successButtonImage,
              })
            : '';

        const cancelButtonHTML = cancelButton
            ? this.getCancelTemplate({
                  handle: handleCancel,
                  text: cancelButton,
                  addClass: cancelClass,
                  cancelAsLabel,
                  img: cancelButtonImage,
              })
            : '';

        const closeButtonHTML = closeButton
            ? this.getCloseTemplate({
                  text: closeButtonText,
                  addClass: closeButtonClass,
              })
            : '';
        return html`
            ${closeButton && !this.closeButtonOutsideContent
                ? html`
                      ${closeButtonHTML}
                  `
                : null}
            ${cancelButton || successButton
                ? html`
                      <div class="dialog__wrapper-button">
                          ${successButtonHTML} ${cancelButtonHTML}
                      </div>
                  `
                : null}
        `;
    }

    getCancelTemplate({ handle, text, addClass, cancelAsLabel, img }) {
        if (cancelAsLabel) {
            return this.getLabelTemplate(handle, text);
        }
        return this.getButtonTemplate({ handle, text, addClass, img });
    }

    getClickTracking(child, gtmType) {
        return html`
            <e2-click-tracking analytics-config="${this.analyticsConfig}" type="${gtmType}">
                ${child}
            </e2-click-tracking>
        `;
    }

    getCloseTemplate({ text, addClass }) {
        const close = html`
            <div class="dialog__button-close ${this.closeButtonClasses}" @click=${this.handleClose}>
                <span class="dialog__button-text">${text}</span>
                <i class="icon ${addClass}"></i>
            </div>
        `;
        if (this.analyticsConfig && this.gtmTypeClose) {
            return this.getClickTracking(close, this.gtmTypeClose);
        }
        return close;
    }

    get closeButtonClasses() {
        return classMap({
            'dialog__button-close--inside-dialog': this.closeButtonInsideDialog,
        });
    }

    getScrollTemplate(content) {
        return this.noScroll
            ? content
            : html`
                  <e2-scroll>${content}</e2-scroll>
              `;
    }

    getButtonTemplate({ handle, text, addClass, img }) {
        return html`
            <e2-button
                class-modifier="${buildBlockModificationClass('button', addClass)}"
                image="${img}"
                size="${this.buttonsSize ? this.buttonsSize : 'big'}"
                @click="${handle}"
                style="${this.fullWidthButton ? 'width: 100%' : ''}"
                ?no-arrow="${this.fullWidthButton || this.noArrow}"
                ?full-width="${this.fullWidthButton}"
            >
                ${text}
            </e2-button>
        `;
    }

    getLabelTemplate(handle, label) {
        return html`
            <a class="dialog__label" @click=${handle}>
                <span class="button__text">${label}</span>
            </a>
        `;
    }

    getCloseButtonTemplate() {
        return html`
            <div class="carousel__close-button">✕</div>
        `;
    }

    handleSuccess(evt) {
        evt.stopPropagation();
        const redirectUrl = this.iframeRedirectOnClose;
        this.doClose();
        if (redirectUrl && this.redirectOnSuccess) {
            this.dispatchEvent(
                new CustomEvent('redirect', {
                    detail: redirectUrl,
                }),
            );
        }
        document.dispatchEvent(new Event(`${this.name}-success`));
        this.dispatchEvent(new Event(`${this.name}-success`));
    }

    handleCancel(evt) {
        evt.stopPropagation();
        this.doClose();
        document.dispatchEvent(new Event(`${this.name}-ko`));
    }

    handleClose(evt) {
        evt.stopPropagation();
        const redirectUrl = this.iframeRedirectOnClose;
        this.doClose();
        if (redirectUrl) {
            this.dispatchEvent(
                new CustomEvent('redirect', {
                    detail: redirectUrl,
                }),
            );
        }
    }

    doClose() {
        if (this.customHandleClose) {
            this.dispatchEvent(
                new CustomEvent('set-custom-handle-close', {
                    detail: false,
                }),
            );
            return;
        }

        this.dispatchEvent(new CustomEvent('close'));

        if (this.iframeStopVideoOnClose) {
            this.handleIframeStopVideoOnClose();
        }
    }

    handleIframeStopVideoOnClose() {
        /* As this component isn't unmounting from the DOM after closing the dialog,
        we need this function to stop the video in dialog iframe if we have it. */

        const iframeComponent = this.iframeComponentSelector
            ? this.querySelector(this.iframeComponentSelector)
            : null;
        const iframe = iframeComponent
            ? iframeComponent.shadowRoot && iframeComponent.shadowRoot.querySelector('iframe')
            : this.shadowRoot.querySelector('iframe');

        if (iframe) {
            const iframeSrc = iframe.src;
            iframe.src = iframeSrc;
        }
    }

    firstUpdated() {
        const dialog = this.shadowRoot.querySelector('[data-element="dialog"]');
        this.dialog = dialog;
        this.initialized = true;
    }

    updated(changedProps) {
        if (changedProps.has('shouldBeOpened') && this.shouldBeOpened) {
            this.dispatchEvent(
                new CustomEvent('open', {
                    detail: {
                        name: this.name,
                    },
                }),
            );
        }
        if (changedProps.has('open')) {
            if (this.open) {
                this.openDialog();
            } else {
                this.closeDialog();
            }
        }
        if (
            this.iframeInitialized &&
            this.adjustIframeHeightToContent &&
            changedProps.has('windowSizeHeight')
        ) {
            this.adjustHeight();
        }
    }

    /*
        Use getBoundingClientRect() to avoid the case when clicking on padding area of dialog
        leads to popup closing
    */
    isInDialog(event) {
        const dialogBoundingRect = this.dialog.getBoundingClientRect();
        return (
            dialogBoundingRect.top <= event.clientY &&
            event.clientY <= dialogBoundingRect.bottom &&
            dialogBoundingRect.left <= event.clientX &&
            event.clientX <= dialogBoundingRect.right
        );
    }

    get pointerIsAtSameArea() {
        const { up, down } = this.pointerIsInDialog;
        return up !== null && down !== null && up === down;
    }

    attachDialogListeners(listenersMap) {
        listenersMap.forEach(([event, listener]) =>
            this.shadowRoot.addEventListener(event, listener),
        );
    }

    detachDialogListeners(listenersMap) {
        listenersMap.forEach(([event, listener]) =>
            this.shadowRoot.removeEventListener(event, listener),
        );
    }

    openDialog() {
        let dialog = this.dialog;
        if (dialog.hasAttribute('open')) {
            return;
        }

        dialog.setAttribute('open', 'open');

        if (!this.allowBodyScroll) bodyScrollLock.enable();
        const blurButton = dialog.querySelector('e2-button');
        const blurActionButton = this.shadowRoot.host.querySelector('e2-action-button');

        if (blurButton) {
            blurButton.blur();
        }

        if (blurActionButton) {
            blurActionButton.blur();
        }

        const mouseDownListener = e => {
            this.pointerIsInDialog.down = this.isInDialog(e);
        };
        const mouseUpListener = e => {
            this.pointerIsInDialog.up = this.isInDialog(e);
        };

        if (this.closeOnBgClick) {
            const backdropEvent = () => {
                if (this.pointerIsInDialog.down && this.pointerIsInDialog.up) {
                    return;
                }

                /*
                    Close dialog only when it is opened and both mouseup + mousedown happened
                    either on the backdrop or on the dialog
                */
                if (this.open && this.pointerIsAtSameArea) {
                    this.detachDialogListeners([
                        ['click', backdropEvent],
                        ['mouseup', mouseUpListener],
                        ['mousedown', mouseDownListener],
                    ]);
                    const redirectUrl = this.iframeRedirectOnClose;
                    this.doClose();

                    document.dispatchEvent(new Event(`${this.name}-closeOnBg`));
                    if (redirectUrl && this.redirectOnSuccess) {
                        this.dispatchEvent(
                            new CustomEvent('redirect', {
                                detail: redirectUrl,
                            }),
                        );
                    }
                }
            };

            this.attachDialogListeners([
                ['click', backdropEvent],
                ['mouseup', mouseUpListener],
                ['mousedown', mouseDownListener],
            ]);
        }

        if (this.closeTimerMs > 0) {
            setTimeout(() => {
                this.doClose();
            }, this.closeTimerMs);
        }

        if (this.showContentFromTop) {
            dialog.scrollTo(0, 0);
        }
    }

    closeDialog() {
        const dialog = this.dialog;
        if (dialog) {
            if (this.animation) {
                dialog.classList.add('hide');
                const animation = () => {
                    dialog.classList.remove('hide');
                    if (dialog.hasAttribute('open')) {
                        dialog.removeAttribute('open');
                    }
                    dialog.removeEventListener('webkitAnimationEnd', animation, false);
                };
                dialog.addEventListener('webkitAnimationEnd', animation, false);
            } else {
                if (dialog.hasAttribute('open')) {
                    dialog.removeAttribute('open');
                }
            }

            if (this.removeStoreCtaDataOnDialogClose && this.name == 'rts-pickup-pdp') {
                this.dispatchEvent(new CustomEvent('CLEAR_STORES'));
                this.dispatchEvent(new CustomEvent('RESET_ERRORS'));
                const storeLocatorInputField = this.querySelector('#search');
                storeLocatorInputField.value = '';
            }

            if (this.closeDispatchAction) {
                this.dispatchEvent(new CustomEvent('close-dispatch-action'));
            }

            if (!this.allowBodyScroll) bodyScrollLock.disable();
        }
    }

    getIframeTemplate() {
        return html`
            <iframe
                width="100%"
                scrolling="auto"
                src="${this.iframeUrl}"
                @load="${this.handleIframeLoad}"
                title="${this.iframeTitle}"
            >
            </iframe>
        `;
    }

    handleIframeLoad({ target: iframe }) {
        if (this.adjustIframeWidthToWidestImg) {
            this.adjustWidth();
        }
        if (this.adjustIframeHeightToContent) {
            this.adjustHeight();
        }
        if (this.openLinksInMainWindow) {
            const linksCollection = Array.from(iframe.contentDocument.getElementsByTagName('a'));
            const e2LinkCollection = Array.from(
                iframe.contentDocument.getElementsByTagName('e2-link'),
            );
            const finalLinksCollection = [...linksCollection, ...e2LinkCollection];

            for (let link of finalLinksCollection) {
                link.addEventListener('click', e => this.handleClickOnLinkInsideIframe(e));
            }
        }
    }

    adjustHeight() {
        const iframe = this.shadowRoot.querySelector('iframe');
        const extraNodesWhoseHeightToAdjust = ['dialog'];
        const nodes = [iframe, ...this.shadowRoot.querySelectorAll(extraNodesWhoseHeightToAdjust)];
        const iframeHeight = iframe.contentDocument.body.scrollHeight;
        const dialogMaxHeight = this.windowSizeHeight * 0.95;
        nodes.forEach(node => {
            node.style.height =
                iframeHeight > dialogMaxHeight ? `${dialogMaxHeight}px` : `${iframeHeight}px`;
        });

        iframe.scrolling = iframeHeight > dialogMaxHeight ? 'yes' : 'no';
        this.iframeInitialized = true;
    }

    adjustWidth() {
        const iframe = this.shadowRoot.querySelector('iframe');
        const iframeImages = iframe.contentWindow.document.body.querySelectorAll('img');
        const widestImage = [...iframeImages].reduce((widest, curr) =>
            curr.naturalWidth > widest.naturalWidth ? curr : widest,
        );
        iframe.width = widestImage.naturalWidth;
    }

    handleClickOnLinkInsideIframe(e) {
        const currentTarget = e.currentTarget;
        e.preventDefault();
        this.doClose();
        this.dispatchEvent(
            new CustomEvent('redirect', {
                detail: currentTarget.href,
            }),
        );
        return false;
    }
}

const mapStateToProps = (state, ctx) => ({
    content: selectors.getDialogContent(state),
    iframeUrl: selectors.getDialogIframeUrl(state),
    iframeRedirectOnClose: selectors.getIframeRedirectOnClose(state),
    open: selectors.getDialogState(state, ctx.name),
    windowSizeHeight: selectors.getWindowSize(state).height,
    customHandleClose: selectors.getCustomHandleClose(state),
    removeStoreCtaDataOnDialogClose: configSelectors.removeStoreCtaDataOnDialogClose(state),
});

const mapDispatchToEvents = (dispatch, ctx) => ({
    open: ({ detail }) => dispatch(actions.openDialog(detail)),
    close: () => dispatch(actions.closeDialogByName(ctx.name)),
    'close-dispatch-action': () => dispatch({ type: ctx.closeDispatchAction }),
    redirect: e => dispatch(globalActions.redirect(e.detail)),
    'set-custom-handle-close': ({ detail }) => {
        dispatch(actions.setCustomHandleClose(detail));
    },
    CLEAR_STORES: () => dispatch(rtsActions.clearStores()),
    RESET_ERRORS: () => {
        dispatch(storeLocatorAction.setErrorState(false));
        dispatch(rtsActions.resetErrors());
    },
});

export default () =>
    customElements.define('e2-dialog', connect(mapStateToProps, mapDispatchToEvents)(Dialog));
